<template>
  <LoadingComponent v-if="loading" />
  <template v-if="!loading && localDashboard">
    <div class="flex-shrink-0">
      <DashboardHeader :dashboard="localDashboard">
        <template #cta>
          <DashboardPanelControl
            v-model:require-save-dashboard="requireSaveDashboard"
            v-model:dashboard="localDashboard"
            :count-filter="validFilter.length ?? 0"
            :need-save-locked="loading"
            @dashboard-changed="dashboardChanged"
          >
            <template #cta>
              <button
                class="flex aspect-square w-9 items-center justify-center rounded-full border border-slate-300"
                @click="subscriptionLinkModalOpen = true"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 640 640"
                  class="mt-1 ml-1 h-5 w-5"
                >
                  <path
                    d="M0 128C0 92.7 28.7 64 64 64l40 0 0-40c0-13.3 10.7-24 24-24s24 10.7 24 24l0 40 144 0 0-40c0-13.3 10.7-24 24-24s24 10.7 24 24l0 40 40 0c35.3 0 64 28.7 64 64l0 16 0 48 0 35c-13-2-26.4-3-40-3c-2.7 0-5.4 .2-8 .6l0-32.6L48 192l0 256c0 8.8 7.2 16 16 16l288 0 0 48L64 512c-35.3 0-64-28.7-64-64L0 192l0-48 0-16zM384 280c0-13.3 10.7-24 24-24c128.1 0 232 103.9 232 232c0 13.3-10.7 24-24 24s-24-10.7-24-24c0-101.6-82.4-184-184-184c-13.3 0-24-10.7-24-24zm0 96c0-13.3 10.7-24 24-24c75.1 0 136 60.9 136 136c0 13.3-10.7 24-24 24s-24-10.7-24-24c0-48.6-39.4-88-88-88c-13.3 0-24-10.7-24-24zm0 104c0-17.7 14.3-32 32-32s32 14.3 32 32s-14.3 32-32 32s-32-14.3-32-32z"
                  ></path>
                </svg>
              </button>
            </template>
            <template #filter-content="{ close }">
              <FilterLinesComponent
                v-model:filters="localDashboard.filters"
                v-model:require-save-dashboard="requireSaveDashboard"
                :filters-available="availableFilter"
                @close-popover="() => closePopover(close)"
              />
            </template>
            <template #display-options-content>
              <DisplayOptionsContent
                v-model:display-options="localDashboard.display_options"
              />
            </template>
          </DashboardPanelControl>
        </template>
      </DashboardHeader>
    </div>
    <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      <FullCalendar ref="fullCalendar" :options="getCalendarOption">
        <template #eventContent="{ timeText, event }">
          <EventComponent
            :event="event"
            :time-text="timeText"
            :display-users-selector="
              [
                'timeGridDay',
                'resourceTimeline',
                'resourceTimelineWeek',
                'resourceTimelineDay',
                'resourceTimelineThreeDays'
              ].includes(localDashboard?.display_options?.view)
            "
          />
        </template>
      </FullCalendar>
    </div>
    <WheelEventColor />
    <EventModal
      v-model:open="isEventModalOpen"
      :selected-event-id="selectedEventId"
      :full-day-event="fullDayEvent"
      :new-event-from="newEventFrom"
      :new-event-to="newEventTo"
      :new-event-place="newEventPlace"
      :new-event-place-type="newEventPlaceType"
      :new-event-place-kind="newEventPlaceKind"
      :is-in-planning="false"
      @close="isEventModalOpen = false"
      @event-updated="fetchEvent"
      @delete-event="handleDeleteEvent"
    />
    <EventModalNew
      v-model:is-modal-open="isEventModalOpenNew"
      :event-id="selectedEventId"
    />
    <GenerateOutlookLinkModal
      v-model:open="subscriptionLinkModalOpen"
      :dashboard="currentBoard"
    />
  </template>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import DashboardHeader from '@c/Dashboard/Components/DashboardHeader.vue'

import FullCalendar from '@fullcalendar/vue3'
import nlLocale from '@fullcalendar/core/locales/nl'
import frLocale from '@fullcalendar/core/locales/fr'
import enLocale from '@fullcalendar/core/locales/en-gb'
import roLocale from '@fullcalendar/core/locales/ro'

import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import resourceTimeLinePlugin from '@fullcalendar/resource-timeline'

import moment from 'moment'
import DashboardPanelControl from '../../Components/DashboardPanelControl.vue'
import DisplayOptionsContent from '@c/Dashboard/Content/Agenda/Panel/DisplayOptionsContent.vue'
import FilterLinesComponent from '@c/Dashboard/Components/FilterLinesComponent.vue'
import dashboard from '@/store/modules/dashboard'
import EventComponent from '@c/Dashboard/Content/Agenda/components/EventComponent.vue'
import EventModal from '@ui/modal/event/EventModal.vue'
import EventModalNew from '@ui/modal/event.new/EventModal.vue'
import WheelEventColor from '@c/WheelEventColor.vue'
import GenerateOutlookLinkModal from '@c/Dashboard/Components/DashboardComponent/GenerateOutlookLinkModal.vue'
import PlaceType from '@/assets/enums/config/PlaceType'

const palette = [
  'enaos',
  'elys',
  'kitty',
  'peppol',
  'chift',
  'legacio',
  'funeup',
  'funebra'
]

export default {
  name: 'AgendaContent',
  components: {
    GenerateOutlookLinkModal,
    WheelEventColor,
    EventModal,
    EventComponent,
    FilterLinesComponent,
    DisplayOptionsContent,
    DashboardPanelControl,
    DashboardHeader,
    LoadingComponent,
    FullCalendar,
    EventModalNew
  },
  props: {
    currentBoard: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      requireSaveDashboard: false,
      subscriptionLinkModalOpen: false,
      fullDayEvent: false,
      isEventModalOpen: false,
      isEventModalOpenNew: false,
      selectedEventId: null,
      newEventFrom: null,
      newEventTo: null,
      newEventPlace: null,
      newEventPlaceType: null,
      newEventPlaceKind: null,
      loading: false,
      localDashboard: null,
      currentStartDate: null,
      currentEndDate: null,
      calendarOption: {
        headerToolbar: {
          start: 'title',
          center: '',
          // end: 'dayGridMonth,timeGridWeek,timeGridDay resourceTimelineDay'
          end: 'today prev,next'
        },
        views: {
          resourceTimelineDay: {
            type: 'resourceTimelineDay',
            duration: { days: 1 },
            buttonText: 'Planning jour',
            titleFormat: {
              month: 'long',
              year: 'numeric',
              day: 'numeric',
              weekday: 'long'
            }
          },
          resourceTimelineThreeDays: {
            type: 'resourceTimeline',
            duration: { days: 3 },
            titleFormat: {
              month: 'long',
              year: 'numeric',
              day: 'numeric',
              weekday: 'long'
            }
          },
          resourceTimelineWeek: {
            type: 'resourceTimelineWeek',
            duration: { days: 7 },
            buttonText: 'Planning semaine',
            titleFormat: {
              month: 'long',
              year: 'numeric',
              day: 'numeric',
              weekday: 'long'
            }
          },
          timeGridWeek: {
            type: 'timeGridWeek',
            minTime: '07:00:00',
            maxTime: '21:00:00',
            buttonText: this.$t('global.week'),
            titleFormat: {
              month: 'short',
              year: 'numeric',
              day: 'numeric'
              // weekday: 'long'
            }
          }
        },
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin,
          listPlugin,
          resourceTimeLinePlugin
        ],
        resourceAreaWidth: '15%',
        height: 'auto',
        nowIndicator: true,
        firstDay: '1',
        editable: true,
        selectable: true,
        droppable: true,
        schedulerLicenseKey: 'CC-Attribution-NonCommercial-NoDerivatives'
      }
    }
  },
  computed: {
    dashboard() {
      return dashboard
    },
    ...mapGetters({
      auth: 'auth/getAuth',
      eventsList: 'agenda/getEvents',
      calendarState: 'agenda/getCalendarState',
      currentTemplate: 'dashboard/getTemplate',
      availableFilter: 'metadata/getAvailableFilterAgenda',
      sites: 'metadata/getSitesMetadata',
      locations: 'metadata/getLocationsMetadata',
      funeralHomes: 'metadata/getFuneralHomesMetadata',
      users: 'metadata/getUsersMetadata'
    }),
    currentView() {
      return this.$refs.fullCalendar.getApi().view
    },
    config() {
      return {
        ...this.calendarOptions,
        ...this.eventHandler
      }
    },
    getFullcalendarLocal() {
      switch (this.$i18n.locale) {
        case 'ro_RO':
          return roLocale
        case 'nl_BE':
          return nlLocale
        case 'fr_BE':
        case 'fr_FR':
          return frLocale
        default:
          return enLocale
      }
    },
    getCalendarOption() {
      let slotDuration = this.localDashboard?.display_options?.slotDuration

      let options = {
        slotMinTime:
          slotDuration === '24:00:00'
            ? '00:00:00'
            : (this.localDashboard?.display_options?.slotTime?.min ??
              '06:00:00'),
        slotMaxTime:
          slotDuration === '24:00:00'
            ? '23:59:00'
            : (this.localDashboard?.display_options?.slotTime?.max ??
              '20:00:00'),
        locale: this.getFullcalendarLocal,
        events: this.eventsFormated,
        slotDuration: slotDuration ?? '00:15:00',
        initialView:
          this.localDashboard?.display_options?.view ?? 'timeGridWeek',
        ...this.calendarOption,
        ...this.eventHandler
      }

      if (
        [
          'resourceTimelineDay',
          'resourceTimelineThreeDays',
          'resourceTimelineWeek',
          'resourceTimeline'
        ].includes(this.localDashboard?.display_options?.view)
      ) {
        options = {
          ...options,
          resources: this.computed_resources,
          resourceOrder: 'title'
        }
      }

      if (this.localDashboard?.display_options?.compactView) {
        options = { ...options, eventMaxStack: 1 }
      }

      return options
    },
    eventHandler() {
      return {
        datesSet: this.handleDateRangeChange,
        // dateClick: this.handleDateClick,
        select: this.handleDateSelect,

        eventClick: this.handleEventClick,
        eventDrop: this.handleEventDrop,
        eventReceive: this.handleEventReceived,
        eventResize: this.handleEventDrop,

        viewClassNames: this.handleViewRender,
        viewDidMount: this.handleViewDidMount
      }
    },

    validFilter() {
      return (
        this.localDashboard.filters?.filter(
          (filter) => filter.column && filter.values && filter.condition
        ) ?? []
      )
    },

    computed_resources() {
      switch (this.localDashboard?.display_options?.timelineGroupBy) {
        case 'site':
          return this.sitesAsResource
        case 'location':
          return this.locationsAsResource
        case 'users':
          return this.usersAsResource
        default:
          return null
      }
    },

    locationsAsResource() {
      const siteResources = this.sites
        .map((site) => {
          return {
            id: site.id,
            title: site.name,
            eventProps: {
              place_id: site.id,
              place_type: PlaceType.FUNERARIUM,
              place: site
            },
            children: this.locations
              .filter((location) => location.site_id === site.id)
              .when(
                this.localDashboard?.display_options?.timelineLocationFilter
                  ?.length,
                (locations) =>
                  locations.filter((location) => {
                    return this.localDashboard.display_options.timelineLocationFilter.includes(
                      location.type
                    )
                  })
              )
              .map((location) => {
                return {
                  id: location.id,
                  title: location.name,
                  eventProps: {
                    place_id: location.id,
                    place: location,
                    place_type: PlaceType.SALLE_FUNERARIUM
                  }
                }
              })
          }
        })
        .filter((site) => site.children.length > 0)

      const visibleFuneralHomeIds = [
        ...new Set(
          this.sites
            .filter((s) => siteResources.some((sr) => sr.id === s.id))
            .map((s) => s.funeral_home_id)
        )
      ]

      const externalResources = this.funeralHomes
        .filter((fh) => visibleFuneralHomeIds.includes(fh.id))
        .map((fh) => ({
          id: `external-fh-${fh.id}`,
          title: this.$t('global.external_events'),
          eventProps: null
        }))

      return [...siteResources, ...externalResources]
    },

    sitesAsResource() {
      return this.funeralHomes.map((funeralHome) => {
        return {
          id: funeralHome.id,
          title: funeralHome.name,
          eventProps: null,
          children: [
            ...this.sites
              .filter((site) => site.funeral_home_id === funeralHome.id)
              .map((site) => {
                return {
                  id: site.id,
                  title: site.name,
                  eventProps: {
                    place_id: site.id,
                    place: site,
                    place_type: PlaceType.FUNERARIUM
                  }
                }
              }),
            {
              id: `external-fh-${funeralHome.id}`,
              title: this.$t('global.external_events'),
              eventProps: null
            }
          ]
        }
      })
    },

    usersAsResource() {
      return this.users.map((user) => {
        return {
          id: user.id,
          title: user.full_name,
          groupId: null
        }
      })
    },

    deceasedColorMap() {
      const uniqueDossierIds = [
        ...new Set(
          this.eventsList
            .map((event) => event.extendedProps?.dossier_id)
            .filter(Boolean)
        )
      ]
      return Object.fromEntries(
        uniqueDossierIds.map((dossierId) => [
          dossierId,
          palette[
            String(dossierId)
              .split('')
              .reduce((acc, char) => acc + char.charCodeAt(0), 0) %
              palette.length
          ]
        ])
      )
    },

    eventsFormated() {
      const colorByDeceased =
        this.localDashboard?.display_options?.colorByDeceased

      const coloredEvents = colorByDeceased
        ? this.eventsList.map((event) => ({
            ...event,
            tint:
              this.deceasedColorMap[event.extendedProps?.dossier_id] ??
              event.tint
          }))
        : this.eventsList

      if (
        [
          'resourceTimelineDay',
          'resourceTimelineThreeDays',
          'resourceTimelineWeek',
          'resourceTimeline'
        ].includes(this.localDashboard?.display_options?.view)
      ) {
        if (this.localDashboard?.display_options?.timelineGroupBy === 'users') {
          return coloredEvents.map((event) => ({
            ...event,
            resourceIds: event.extendedProps?.users?.map((user) => user.id)
          }))
        } else {
          return coloredEvents.map((event) => {
            const resourceId =
              event.extendedProps.site_id ??
              event.extendedProps.location_id ??
              (this.isExternalEvent(event)
                ? `external-fh-${event.extendedProps.funeral_home_id}`
                : null)
            return { ...event, resourceId }
          })
        }
      }
      return coloredEvents
    }
  },
  watch: {
    currentBoard(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchAgendaDashboardConfig()
      }
    },
    'localDashboard.display_options.view'(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.changeFullCalendarView(newValue)
      }
    },
    'localDashboard.display_options.slotTime'(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.changeFullCalendarSlotTime(newValue)
      }
    },
    'localDashboard.display_options.colorByDeceased'(_newValue, oldValue) {
      if (oldValue !== undefined) {
        this.requireSaveDashboard = true
      }
    },
    'localDashboard.display_options.compactView'(_newValue, oldValue) {
      if (oldValue !== undefined) {
        this.requireSaveDashboard = true
      }
    }
    // 'localDashboard.filters': {
    //   handler(newValue, oldValue) {
    //     if (this.$refs.fullCalendar && newValue) {
    //       this.fetchEvent(this.currentStartDate, this.currentEndDate)
    //     }
    //   },
    //   deep: true
    // }
  },
  mounted() {
    this.fetchAgendaDashboardConfig()
  },
  methods: {
    ...mapActions({
      processFetchTemplateDashboardByIdAction:
        'dashboard/fetchTemplateDashboardById',
      processFetchEventsAction: 'agenda/fetchEvents',
      processFetchUnplannedEventsAction: 'agenda/fetchUnplannedEvents',
      processUpdateEventByDragAction: 'agenda/updateEventByDrag',
      processDeleteEventByIdAction: 'planning/deleteEvent',
      processUndoDeleteEventByIdAction: 'planning/undoDeleteEvent'
    }),
    ...mapMutations({
      processSetTemplateMutation: 'dashboard/setTemplate',
      processSetCalendarStateMutation: 'agenda/setCalendarState'
    }),

    isExternalEvent(event) {
      const placeKind = event.extendedProps?.place_kind
      return placeKind != null && placeKind !== 'FUN' && placeKind !== 'SFU'
    },

    handleViewDidMount(event) {
      // this.fetchEvent(
      //   moment(event.view.activeStart).format('DD/MM/YYYY'),
      //   moment(event.view.activeEnd).format('DD/MM/YYYY')
      // )
    },

    handleDateRangeChange(event) {
      this.currentStartDate = moment(event.startStr)
      this.currentEndDate = moment(event.endStr).add(-1, 'second')
      this.fetchEvent(this.currentStartDate, this.currentEndDate)
    },
    handleEventClick({ event }) {
      this.selectedEventId = event.id
      this.fullDayEvent = false
      this.newEventFrom = null
      this.newEventTo = null
      this.isEventModalOpen = true
      // if (this.$can('super-admin')) {
      //   this.isEventModalOpenNew = true
      // } else {
      //   this.isEventModalOpen = true
      // }
    },

    handleEventDrop(obj) {
      this.processUpdateEventByDragAction({
        date_from: obj.event.startStr,
        date_to: obj.event.endStr,
        id: obj.event.id,
        successCallback: () =>
          this.$h.toastSuccess(this.$t('toasts.event_updated'))
      })
    },

    fetchAgendaDashboardConfig() {
      this.loading = true
      const targetId = this.currentBoard.id
      this.processFetchTemplateDashboardByIdAction({ id: targetId })
        .then((response) => {
          if (this.currentBoard.id !== targetId) return
          this.localDashboard = response.template_dashboard
        })
        .catch((error) => {
          this.$router.push({ name: 'dashboard_main_page_route' })
        })
        .finally((_) => {
          if (this.currentBoard.id === targetId) {
            this.loading = false
            this.$nextTick(() => {
              this.requireSaveDashboard = false
            })
          }
        })
    },

    changeFullCalendarView(view) {
      if (this.$refs.fullCalendar) {
        this.$refs.fullCalendar.getApi().changeView(view)
      }
    },
    changeFullCalendarSlotTime(slotTime) {
      if (this.$refs.fullCalendar) {
        this.$refs.fullCalendar.getApi().setOption('slotMinTime', slotTime.min)
        this.$refs.fullCalendar.getApi().setOption('slotMaxTime', slotTime.max)
      }
    },

    fetchEvent(start, end) {
      start = start !== undefined ? start : this.currentStartDate
      end = end !== undefined ? end : this.currentEndDate
      this.processFetchEventsAction({
        start: start,
        end: end,
        filters: this.validFilter,
        include_occupation: [
          'timeGridDay',
          'resourceTimeline',
          'resourceTimelineWeek',
          'resourceTimelineDay',
          'resourceTimelineThreeDays'
        ].includes(this.localDashboard?.display_options?.view)
      })
    },

    handleDateSelect(event) {
      this.selectedEventId = null

      if (!event.allDay) {
        this.newEventFrom = moment(event.start).format()
        this.newEventTo = moment(event.end).format()
        // this.newEventPlaceType =
        //   event?.resource?.extendedProps?.eventProps?.place_type ?? null
        this.newEventPlaceKind =
          event?.resource?.extendedProps?.eventProps?.place_type ?? null
        this.newEventPlace =
          event?.resource?.extendedProps?.eventProps?.place ?? null
        this.fullDayEvent = false
      } else {
        this.newEventFrom = moment(event.start).startOf('day')
        this.newEventTo = this.newEventFrom.clone().endOf('day')
        // this.newEventPlaceType =
        //   event?.resource?.extendedProps?.eventProps?.place_type ?? null
        this.newEventPlaceKind =
          event?.resource?.extendedProps?.eventProps?.place_type ?? null
        this.newEventPlace =
          event?.resource?.extendedProps?.eventProps?.place ?? null
        this.fullDayEvent = true
      }

      this.isEventModalOpen = true
    },

    closePopover(close) {
      if (typeof close === 'function') {
        close()
      }
      this.fetchEvent(this.currentStartDate, this.currentEndDate)
    },

    dashboardChanged(_dashboard_template_id) {
      this.fetchAgendaDashboardConfig()
    },
    handleDeleteEvent(event_id) {
      this.processDeleteEventByIdAction({
        id: event_id
      }).then((response) => {
        this.fetchEvent(this.currentStartDate, this.currentEndDate)
        this.$h.toastAction(
          'delete',
          this.$t('toasts.deleted_v', { var: this.$t('attributes.event') }),
          null,
          [
            {
              title: this.$t('button.cancel'),
              class: 'text-red-700',
              action: () => {
                this.undoDelete(event_id)
              }
            }
          ]
        )
      })
    },
    undoDelete(id) {
      this.processUndoDeleteEventByIdAction({
        id: id
      }).then((response) => {
        this.fetchEvent(this.currentStartDate, this.currentEndDate)
        this.$h.toastSuccess(
          this.$t('toasts.reseted_v', {
            var: this.$t('attributes.event')
          })
        )
      })
    }
  }
}
</script>

<style>
.fc-header-toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
}
</style>
